import type { ActivityItem } from '../ActivityThread/activity-thread-types'
import { activityDuration, toActivityItems } from './chat-activity'
import { dedupeOutcomes, startedDesignEdits, withoutDesignEdits } from './chat-outcomes'
import type { AgentOutcome, AgentSource, ChatMessage, TranscriptMessageRole } from '@/types/chat'

/** A message the transcript shows: every role but the model's own `system` memory. */
type TranscriptMessage = ChatMessage & { role: TranscriptMessageRole }

/** A turn's activity card: live while its turn streams, then the same card once the turn is saved. */
interface ActivityRow {
  kind: 'activity'
  key: string
  from: TranscriptMessageRole
  items: ActivityItem[]
  duration?: number
  streaming: boolean
}

interface MessageRow {
  kind: 'message'
  key: string
  message: TranscriptMessage
}

/** The outcome rows a saved reply's steps carried. */
interface OutcomesRow {
  kind: 'outcomes'
  key: string
  from: TranscriptMessageRole
  outcomes: AgentOutcome[]
}

interface SourcesRow {
  kind: 'sources'
  key: string
  from: TranscriptMessageRole
  sources: AgentSource[]
}

/** Raldo's edit of a design, started in this reply and the run that edits it. */
interface DesignEditRow {
  kind: 'designEdit'
  key: string
  from: TranscriptMessageRole
  designId: string
  runId: string
}

export type TranscriptRow = ActivityRow | MessageRow | OutcomesRow | SourcesRow | DesignEditRow

export interface TranscriptRowsInput {
  messages: readonly ChatMessage[]
  liveSteps: readonly ActivityItem[]
  streaming: boolean
  /** Steps of the turn that just ended, shown until its saved message arrives. */
  endedSteps: readonly ActivityItem[]
  /** The reply is awaited and no step has arrived: the thread shows its opening state. */
  awaiting?: boolean
  /** One key for the live turn's card from its first moment until its saved reply, so the card never remounts. */
  turnKey?: string
  /** The key a saved reply's card keeps, by the id of its first step, when the card began as a live turn. */
  keyAliases?: ReadonlyMap<string, string>
}

export function activityKey(firstStepId: string): string {
  return `activity:${firstStepId}`
}

/** One list per reply, in the order the turn read them, with a page named once however many steps read it. */
function sourcesOf(message: ChatMessage): AgentSource[] {
  const sources = (message.steps ?? []).flatMap((step) => step.sources ?? [])
  return [...new Map(sources.map((source) => [source.ref, source])).values()]
}

/** One outcome row per tool call worth showing, in call order. */
function outcomesOf(message: ChatMessage): AgentOutcome[] {
  const outcomes = (message.steps ?? []).flatMap((step) => (step.outcome ? [step.outcome] : []))
  return dedupeOutcomes(outcomes)
}

function settleUnfinished(step: ActivityItem): ActivityItem {
  return step.status === 'active' ? { ...step, status: 'pending' } : step
}

function isShown(message: ChatMessage): message is TranscriptMessage {
  return message.role !== 'system'
}

function messageRows(
  message: TranscriptMessage,
  keyAliases: ReadonlyMap<string, string> | undefined,
): TranscriptRow[] {
  const rows: TranscriptRow[] = []
  const firstStep = message.steps?.[0]
  if (message.steps && firstStep) {
    rows.push({
      kind: 'activity',
      key: keyAliases?.get(firstStep.id) ?? activityKey(firstStep.id),
      from: message.role,
      items: toActivityItems(message.steps),
      duration: activityDuration(message.steps),
      streaming: false,
    })
  }
  rows.push({ kind: 'message', key: message.id, message })
  const allOutcomes = outcomesOf(message)
  const outcomes = withoutDesignEdits(allOutcomes)
  if (outcomes.length) {
    rows.push({ kind: 'outcomes', key: `outcomes:${message.id}`, from: message.role, outcomes })
  }
  for (const { designId, runId } of startedDesignEdits(allOutcomes)) {
    rows.push({
      kind: 'designEdit',
      key: `design-edit:${message.id}:${runId}`,
      from: message.role,
      designId,
      runId,
    })
  }
  const sources = sourcesOf(message)
  if (sources.length) {
    rows.push({ kind: 'sources', key: `sources:${message.id}`, from: message.role, sources })
  }
  return rows
}

export function buildTranscriptRows(input: TranscriptRowsInput): TranscriptRow[] {
  const shown = input.messages.filter(isShown)
  const rows = shown.flatMap((message) => messageRows(message, input.keyAliases))
  const steps = input.streaming ? input.liveSteps : input.endedSteps
  const first = steps[0]
  if (!first) {
    if (input.streaming && input.awaiting) {
      rows.push({
        kind: 'activity',
        key: input.turnKey ?? 'activity:awaiting',
        from: 'assistant',
        items: [],
        streaming: true,
      })
    }
    return rows
  }
  const key = input.turnKey ?? activityKey(first.id)
  if (rows.some((row) => row.key === key)) return rows
  rows.push({
    kind: 'activity',
    key,
    from: 'assistant',
    items: input.streaming ? [...steps] : steps.map(settleUnfinished),
    streaming: input.streaming,
  })
  return rows
}
